import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { Tags } from "lucide-react";
import { Button } from "../../components/Button";
import { useToast } from "../../components/Toast";
import { api } from "../../api/client";

// 報價單列表的「分類」:一組快捷勾選(草稿、已寄出、未結案、已結案、未收款、已收款)。
//
// ── 每個人一份 ──────────────────────────────────────────────────
//
// 業主 2026-10-05:勾選存在**自己的帳號上**(`/api/users/me/list-categories`),
// 與「顯示欄位」同一種所有權 —— 換一台電腦、重新登入都跟著人走,別人的不受影響。
//
// ── 這一支不認得任何一個中文標題 ──────────────────────────────────
//
// 分類的鍵與標題只在後端 `app/services/quote_categories.py` 維護一份
// (狀態那幾個字沿用 `GET /api/quote-statuses`),隨設定一起下來。
// **哪些人看得到「未收款 / 已收款」也是後端說了算**:同仁拿到的清單裡就沒有那兩個,
// 前端只是照畫;真正的把關在後端(送了也是 403)。
//
// ── 什麼時候存 ──────────────────────────────────────────────────
//
// **關起浮層時才存**(點外面、按「完成」或 Esc 都算)。一勾就存的話,連勾三個
// 就是三次請求、列表連跳三次;而且中途的組合(只勾了「未收款」還沒勾「未結案」)
// 會被存成使用者的設定。沒有任何改動時關起來不送請求。

const PANEL_WIDTH = 248;
const PANEL_HEIGHT = 380;

export interface CategoryPickerProps {
  /** 目前存著的分類鍵(後端的順序)。 */
  selected: string[];
  /** 這個人可以勾的分類:鍵 → 標題,順序就是畫面順序,來自後端。 */
  available: Record<string, string>;
  /** 存檔成功後把後端回的新勾選交回列表,列表當場跟著重撈。 */
  onSaved: (next: string[]) => void;
}

/** `GET/PUT /api/users/me/list-categories` 的回應。 */
export interface ListCategorySettings {
  quote_list_categories?: string[];
  available_categories?: Record<string, string>;
}

/** 按鈕上的字:沒勾就只有「分類」,勾了接在冒號後面,照畫面順序用頓號隔開。 */
export function categoryLabel(selected: string[], available: Record<string, string>): string {
  const names = Object.keys(available)
    .filter((key) => selected.includes(key))
    .map((key) => available[key]);
  return names.length > 0 ? `分類:${names.join("、")}` : "分類";
}

export function CategoryPicker({ selected, available, onSaved }: CategoryPickerProps) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<CSSProperties | null>(null);
  const [chosen, setChosen] = useState<string[]>(selected);
  const [saving, setSaving] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const chosenRef = useRef<string[]>(selected);
  const panelId = `nq-category-picker-${useId()}`;
  const keys = Object.keys(available);
  const keysId = keys.join(",");

  const place = useCallback(() => {
    const anchor = triggerRef.current;
    if (!anchor || typeof window === "undefined" || typeof anchor.getBoundingClientRect !== "function") return;
    const rect = anchor.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const flipUp = spaceBelow < PANEL_HEIGHT && rect.top > spaceBelow;
    // 手機上按鈕可能在最左邊:靠左對齊按鈕,再夾回視窗裡,不往畫面外長。
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8));
    setStyle({
      position: "fixed",
      left,
      width: PANEL_WIDTH,
      maxWidth: "calc(100vw - 1rem)",
      // 矮螢幕(手機橫放)放不下就自己捲,「完成」不會掉出畫面
      maxHeight: Math.max(160, (flipUp ? rect.top : window.innerHeight - rect.bottom) - 12),
      overflowY: "auto",
      ...(flipUp
        ? { bottom: Math.max(8, window.innerHeight - rect.top + 4) }
        : { top: rect.bottom + 4 }),
    });
  }, []);

  const update = (next: string[]) => {
    chosenRef.current = next;
    setChosen(next);
  };

  /** 關起來;有改動才送出去。 */
  const closeAndSave = useCallback(
    (restoreFocus: boolean) => {
      setOpen(false);
      if (restoreFocus) triggerRef.current?.focus();
      const next = keysId.split(",").filter((key) => chosenRef.current.includes(key));
      const same = next.length === selected.length && next.every((key) => selected.includes(key));
      if (same) return;
      setSaving(true);
      api
        .put<ListCategorySettings>("/users/me/list-categories", { quote_list_categories: next })
        .then((updated) => onSaved(updated?.quote_list_categories ?? next))
        .catch((err) => {
          // 沒存成就退回存著的那一份,按鈕上的字才不會與真正生效的條件對不起來。
          chosenRef.current = selected;
          setChosen(selected);
          toast.push(err instanceof Error ? err.message : "儲存失敗", "error");
        })
        .finally(() => setSaving(false));
    },
    [keysId, selected, onSaved, toast],
  );

  const toggleOpen = () => {
    if (open) {
      closeAndSave(true);
      return;
    }
    update(selected);
    place();
    setOpen(true);
  };

  useEffect(() => {
    if (!open || typeof window === "undefined") return;
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open || typeof document === "undefined") return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (triggerRef.current?.contains(target)) return;
      if (document.getElementById(panelId)?.contains(target)) return;
      closeAndSave(false);
    };
    document.addEventListener("mousedown", onPointerDown, true);
    return () => document.removeEventListener("mousedown", onPointerDown, true);
  }, [open, panelId, closeAndSave]);

  const onPanelKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeAndSave(true);
    }
  };

  const toggle = (key: string) =>
    update(
      chosenRef.current.includes(key)
        ? chosenRef.current.filter((one) => one !== key)
        : [...chosenRef.current, key],
    );

  // 後端的清單要不到(或還沒到)就沒有東西可勾,整顆按鈕不畫 —— 列表本身照常。
  if (keys.length === 0) return null;

  const panel =
    open && style !== null && typeof document !== "undefined"
      ? createPortal(
          <div
            id={panelId}
            role="dialog"
            aria-label="分類"
            style={style}
            onKeyDown={onPanelKeyDown}
            className={
              "z-50 flex flex-col gap-2 rounded-[var(--radius)] border border-[var(--border-strong)] " +
              "bg-[var(--surface)] p-2 shadow-card"
            }
          >
            <p className="m-0 px-0.5 text-xs text-[var(--text-3)]">
              同一組內勾幾個都算,不同組之間要同時符合。什麼都不勾就是全部。
            </p>
            <ul className="m-0 flex list-none flex-col p-0">
              {keys.map((key) => (
                <li key={key}>
                  {/* 一列至少 40px 高,手機上手指點得到。 */}
                  <label className="flex min-h-10 cursor-pointer items-center gap-2 rounded px-1 text-sm text-[var(--text)] hover:bg-[var(--surface-2)]">
                    <input
                      type="checkbox"
                      checked={chosen.includes(key)}
                      onChange={() => toggle(key)}
                      className="h-4 w-4 shrink-0 accent-[var(--accent)]"
                    />
                    <span className="truncate">{available[key]}</span>
                  </label>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between gap-2 border-t border-[var(--border)] pt-2">
              <Button type="button" variant="ghost" size="sm" disabled={chosen.length === 0} onClick={() => update([])}>
                清除
              </Button>
              <Button type="button" variant="primary" size="sm" onClick={() => closeAndSave(true)}>
                完成
              </Button>
            </div>
          </div>,
          document.body,
        )
      : null;

  const label = categoryLabel(selected, available);
  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="secondary"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={label}
        loading={saving}
        onClick={toggleOpen}
        icon={<Tags aria-hidden="true" size={16} />}
        // 勾了很多時按鈕上的字會很長:在手機上截斷,完整的字放在 title 與 aria-label。
        className="max-w-full sm:max-w-[22rem]"
        title={label}
      >
        <span className="truncate">{label}</span>
      </Button>
      {panel}
    </>
  );
}
